import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  GroupEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Avatar,
  ChatIcon,
  InfoCard,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useCallback } from 'react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import Alert from '@material-ui/lab/Alert';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import GroupIcon from '@material-ui/icons/Group';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';

const SLACK_ANNOTATION = 'neo4j/slack-channel-id';
const OKTA_ANNOTATION = 'neo4j/okta-group-name';

const CardTitle = (props: { title: string }) => (
  <Box display="flex" alignItems="center">
    <GroupIcon fontSize="inherit" />
    <Box ml={1}>{props.title}</Box>
  </Box>
);

export const GroupProfileCard = (props: {
  variant?: InfoCardVariants;
  showLinks?: boolean;
}) => {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const { entity: group } = useEntity<GroupEntity>();
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(group));
    alertApi.post({
      message: 'Refresh scheduled',
      severity: 'info',
      display: 'transient',
    });
  }, [catalogApi, alertApi, group]);

  if (!group) {
    return <Alert severity="error">Group not found</Alert>;
  }

  const {
    metadata: { name, description, title, annotations },
    spec: { profile },
  } = group;

  const entityLocation = annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const entityMetadataEditUrl =
    group.metadata.annotations?.[ANNOTATION_EDIT_URL];

  const slackChannelUrl = group.metadata.annotations?.[SLACK_ANNOTATION]
    ? `https://neo4j.slack.com/archives/${group.metadata.annotations?.[SLACK_ANNOTATION]}`
    : undefined;

  const oktaGroupName = group.metadata.annotations?.[OKTA_ANNOTATION];

  const displayName = profile?.displayName ?? title ?? name;
  const infoCardAction = entityMetadataEditUrl ? (
    <IconButton
      aria-label="Edit"
      title="Edit Metadata"
      component={Link}
      to={entityMetadataEditUrl}
    >
      <EditIcon />
    </IconButton>
  ) : (
    <IconButton aria-label="Edit" disabled title="Edit Metadata">
      <EditIcon />
    </IconButton>
  );

  return (
    <InfoCard
      title={<CardTitle title={displayName} />}
      subheader={description}
      variant={props.variant}
      action={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          {infoCardAction}
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={2} xl={1}>
          <Avatar displayName={displayName} picture={profile?.picture} />
        </Grid>
        <Grid item md={10} xl={11}>
          <List>
            {slackChannelUrl && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Slack Channel">
                    <ChatIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Link to={slackChannelUrl} target="_blank">
                      Slack channel
                    </Link>
                  }
                />
              </ListItem>
            )}
            {oktaGroupName && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Okta Group">
                    <GroupIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={oktaGroupName} secondary="Okta Group" />
              </ListItem>
            )}
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Entity Ref">
                  <GroupIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={stringifyEntityRef(group)}
                secondary="Entity Ref"
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
